<template>
  <div>
    <tk-ask-us-dialog
      v-model="showAskUsDialog"
      topic="shipping"
      :country_id="$cart.country.id"
      show-already-code-btn
      @hasCode="() => showDeliveryCodeField = true"
    />

    <aland-delivery-dialog v-model="showAlandDeliveryDialog" />

    <base-card class="pa-4" :class="{'loading': loading}" color="accent">
      <!-- When cart details request return error-->
      <div class="my-3" v-if="errorMessages.length">
        <div class="red--text my-1" v-for="(error, i) in errorMessages" :key="i">{{ error }}</div>
      </div>

      <!-- Country dropdown -->
      <tk-country-select
        v-model="$cart.country"
        @loadingEnded="onCountryLoadingEnded"
        @change="onCountryChange"
        return-object
      />
      <div
        v-if="!$cart.country.id"
        class="subheading text-xs-center my-3"
      >{{ $t('CART.CHOOSE_COUNTRY')}}</div>

      <!-- Cart subtotal -->
      <subtotal v-else :cartDetails="cartDetails" />

      <!-- Delivery code -->
      <delivery-code
        class="pt-3"
        v-if="showDeliveryCodeField"
        @onDeliverySuccess="(e) => deliveryCode = e"
      />

      <!-- Discount code -->
      <discount-code
        :discountAmount="cartDetails.discount_coupon ? cartDetails.discount_coupon.discount_amount: null"
        @onDiscountSuccess="(e) => discountCode = e"
      />

      <!-- Gift card code -->
      <gift-code @onGiftCodeSuccess="(e) => giftcardCode  = e" />

      <!-- Total sum-->
      <total-sum :cartDetails="cartDetails" />

      <!-- Coprate client / Terms checkbox -->
      <div class="text-xs-right">
        <v-checkbox
          color="primary"
          v-model="$cart.isBusiness"
          :ripple="false"
          :label="$t('CART.CORPORATE_CLIENT')"
          hide-details
        />
        <v-checkbox v-model="termsAccepted" hide-details color="primary" :ripple="false">
          <template v-slot:label>
            <!-- <div v-html="$t('CART.ACCEPT_TERMS') + '.'"></div> -->
            <span>{{ $t('CART.ACCEPT_TERMS') }}</span>
            <span
              @click="showPageDialog('termsconditions')"
              class="terms-link"
            >{{ $t('CART.TERMS_CONDITIONS') }}</span>
          </template>
        </v-checkbox>
      </div>

      <!-- Checkout button -->
      <tk-btn
        class="mt-5 mb-3"
        color="primary"
        :disabled="!isCartReadyToProceed"
        block
        large
        @click="placeOrder"
      >{{ $t('CART.CHECKOUT') }}</tk-btn>

      <!-- Delivery and payment -->
      <div class="action-btns">
        <v-list dense>
          <v-list-tile @click="showPageDialog('delivery')">
            <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.DELIVERY') }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile @click="showPageDialog('payment')">
            <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.PAYMENT') }}</v-list-tile-title>
          </v-list-tile>
          <v-divider />
          <v-list-tile @click="showPageDialog('returns')">
            <v-list-tile-title class="text-uppercase">{{ $t('ARTWORK.RETURNS') }}</v-list-tile-title>
          </v-list-tile>
        </v-list>
      </div>
    </base-card>
  </div>
</template>

<script>
import { ArtworkTransformer } from "../../services/artwork/ArtworkTransformer";
import { useGTag } from '../../services/google/google-tag-manager'

export default {
  components: {
    TkCountrySelect: () => import("@/components/core/TkCountrySelect"),
    TkAskUsDialog: () => import("@/components/core/TkAskUsDialog"),
    DiscountCode: () => import("./DiscountCode"),
    GiftCode: () => import("./GiftCode"),
    DeliveryCode: () => import("./DeliveryCode"),
    Subtotal: () => import("./Subtotal"),
    TotalSum: () => import("./TotalSum"),
    AlandDeliveryDialog: () => import("./AlandDeliveryDialog"),
  },
  data() {
    return {
      errorMessages: [],
      termsAccepted: false,
      loading: false,
      showAskUsDialog: false,
      showDeliveryCodeField: false,

      showAlandDeliveryDialog: false,

      discountCode: null, // Discount text field
      giftcardCode: null, // Giftcard text field
      deliveryCode: null, // Delivery code text field

      // Store the response from cart details request
      cartDetails: {
        country: {},
        delivery: {},
        subtotal: {},
        total: {},
        discount_coupon: {},
      },
    };
  },

  computed: {
    isCartReadyToProceed() {
      const canProceed =
        this.loading === false
        && this.errorMessages.length === 0
        && this.$cart.country.id
        && this.termsAccepted;

      // If delivery is not included, there should be some delivery amount before proceeding
      if (!this.cartDetails.is_delivery_included) {
        return canProceed && this.cartDetails.delivery.amount;
      }

      return canProceed;
    },
  },

  watch: {
    "$cart.items.length": "getCartDetails",
    "$cart.country": "getCartDetails",
    "$cart.isBusiness": "getCartDetails",
    deliveryCode: "getCartDetails",
    discountCode: "getCartDetails",
    giftcardCode: "getCartDetails",
  },

  created() {
    this.getCartDetails();
    useGTag().viewCart(this.$cart.items)
  },

  methods: {
    showPageDialog(slug) {
      this.$app.pageDialog = { show: true, slug: slug };
    },

    onCountryLoadingEnded(countries) {
      if (this.$app.userCountry !== "FI" || this.$cart.country.id) {
        return;
      }
      this.$cart.country = countries.find(
        (country) => country.code === this.$app.userCountry
      );
    },

    onCountryChange(e) {
      const { code } = e;
      console.log("onCountryChange", code);
      this.showDeliveryCodeField = false;
      this.cartDetails.delivery.amount = "-";
      this.deliveryCode = null;
      this.$cart.delivery_code = null;

      if(code === 'AX') {
        this.showAlandDeliveryDialog = true;
      }
    },

    resetAllErrors() {
      this.errorMessages = [];
    },

    handleError(error) {
      if (error.data && error.data.errors) {
        const errors = error.data.errors;
        Object.keys(errors).forEach((key) => {
          console.log({ key });
          errors[key].forEach((e) => {
            this.errorMessages.push(e);
          });
        });
      }
    },

    // Updates our cart service and redirects to checkout page
    placeOrder() {

      if (this.discountCode) {
        this.$cart.discount_coupon_code = this.discountCode;
      }
      if (this.giftcardCode) {
        this.$cart.gift_card_code = this.giftcardCode;
      }
      if (this.deliveryCode) {
        this.$cart.delivery_code = this.deliveryCode;
      }
      useGTag().beginCheckout(this.cartDetails)
      this.$emit('checkout')
      this.$router.push({ name: "checkout" });
    },

    async getCartDetails() {
      console.log("GetCartDetails", this.$cart);
      if (!this.$cart.country.id) return;
      this.loading = true;

      this.resetAllErrors();

      try {
        // Prepare payload
        const payload = {
          artworks: this.$cart.items.map((item) => item.id),
          is_business: this.$cart.isBusiness,
          country_id: this.$cart.country.id,
        };

        // Conditionally add codes
        if (this.giftcardCode) payload.gift_card = this.giftcardCode;
        if (this.discountCode) payload.discount_coupon = this.discountCode;
        if (this.deliveryCode) payload.delivery_code = this.deliveryCode;

        // Request cart details
        const res = await this.$axios.post(`/cart/details`, payload);
        const data = res.data["data"];
        this.cartDetails = { ...this.cartDetails, ...data };

        // Also update the cart items, they might have changed
        const resArtworks = res.data["data"]["artworks"];
        this.$cart.updateCartArtworks(
          resArtworks.map((item) => new ArtworkTransformer().toClass(item))
        );
        console.log("Carts updated", this.$cart.items);

        if (this.shouldShowAskUsDialog()) this.showAskUsDialog = true;

      } catch (error) {
        this.handleError(error);
      }
      this.loading = false;
    },

    /**
     *  This method could be called multiple times, though the user has already
     *  entered the delivery code. Better to check if the delivery amount is set
     */
    shouldShowAskUsDialog() {
      return (
        !this.cartDetails.country.is_delivery_available &&
        !this.cartDetails.delivery.amount
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.terms-link {
  margin-left: 4px;
  text-decoration: underline;
  cursor: pointer;
  &:hover {
    text-decoration: none;
  }
}
</style>
